import { BackdropLoading } from '@/components/backdrop-loading'
import { ModalActions, ModalContainer } from '@/components/container/container'
import RichTextEditor from '@/components/rich-text-editor'
import '@/components/rich-text-editor/styles.css'
import type {
    EmailAccount,
    EmailContact,
    EmailDraftDetail,
    EmailMessageDetail,
    EmailTemplate,
} from '@/lib/api/administrative/inbox-api'
import {
    forwardEmail,
    getAccountSignature,
    getEmailTemplates,
    replyToEmail,
    saveDraft,
    searchEmailContacts,
    sendDraftEmail,
    sendEmail,
} from '@/lib/api/administrative/inbox-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAttachFile,
    MdClose,
    MdDelete,
    MdExpandLess,
    MdExpandMore,
    MdSave,
    MdSend
} from 'react-icons/md'
import { toast } from 'react-toastify'

export type ComposeMode = 'compose' | 'reply' | 'replyAll' | 'forward'

const DEFAULT_EDITOR_HEIGHT = 170

interface ComposeModalProps {
    isOpen: boolean
    onClose: () => void
    onSent: () => void
    accounts: EmailAccount[]
    mode: ComposeMode
    originalMessage?: EmailMessageDetail | null
    draftData?: EmailDraftDetail | null
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function buildQuotedHtml(msg: EmailMessageDetail): string {
    const date = msg.receivedAt
        ? new Date(msg.receivedAt).toLocaleString('pt-BR')
        : ''
    const from = msg.fromName
        ? `${msg.fromName} &lt;${msg.fromAddress}&gt;`
        : msg.fromAddress || ''

    return `
        <br/><br/>
        <div style="border-left:3px solid #ccc;padding-left:12px;margin-left:4px;color:#555;">
            <p style="margin:0 0 8px 0;font-size:12px;">
                Em ${date}, ${from} escreveu:
            </p>
            ${msg.bodyHtml || `<pre>${msg.bodyText || ''}</pre>`}
        </div>
    `
}

function parseAddresses(str?: string): string[] {
    if (!str) return []
    return str
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
}

interface EmailInputProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
}

function EmailInput({ value, onChange, placeholder, disabled }: EmailInputProps) {
    const [suggestions, setSuggestions] = useState<EmailContact[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [highlightIndex, setHighlightIndex] = useState(-1)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const getCurrentToken = useCallback(() => {
        const parts = value.split(',')
        return parts[parts.length - 1]?.trim() || ''
    }, [value])

    const handleChange = (newValue: string) => {
        onChange(newValue)
        const token = newValue.split(',').pop()?.trim() || ''

        if (debounceRef.current) clearTimeout(debounceRef.current)

        if (token.length >= 1) {
            debounceRef.current = setTimeout(async () => {
                const res = await searchEmailContacts(token)
                if (res.result) {
                    setSuggestions(res.result)
                    setShowSuggestions(res.result.length > 0)
                    setHighlightIndex(-1)
                }
            }, 300)
        } else {
            setSuggestions([])
            setShowSuggestions(false)
        }
    }

    const selectSuggestion = (contact: EmailContact) => {
        const parts = value.split(',').map(s => s.trim()).filter(Boolean)
        parts.pop()
        parts.push(contact.email)
        onChange(parts.join(', '))
        setSuggestions([])
        setShowSuggestions(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || suggestions.length === 0) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightIndex(prev =>
                prev < suggestions.length - 1 ? prev + 1 : 0,
            )
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightIndex(prev =>
                prev > 0 ? prev - 1 : suggestions.length - 1,
            )
        } else if (e.key === 'Enter' && highlightIndex >= 0) {
            e.preventDefault()
            selectSuggestion(suggestions[highlightIndex])
        } else if (e.key === 'Escape') {
            setShowSuggestions(false)
        }
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div ref={wrapperRef} className="relative w-full">
            <input
                type="text"
                value={value}
                onChange={e => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    const token = getCurrentToken()
                    if (token.length >= 1 && suggestions.length > 0) {
                        setShowSuggestions(true)
                    }
                }}
                placeholder={placeholder}
                className="input input-bordered input-sm w-full"
                autoComplete="off"
                name={`email-${Math.random().toString(36).slice(2)}`}
                disabled={disabled}
            />
            {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {suggestions.map((contact, idx) => (
                        <li
                            key={contact.email}
                            className={`px-3 py-1.5 cursor-pointer text-sm hover:bg-base-200 flex items-center justify-between ${idx === highlightIndex ? 'bg-base-200' : ''
                                }`}
                            onMouseDown={e => {
                                e.preventDefault()
                                selectSuggestion(contact)
                            }}
                        >
                            <div className="min-w-0">
                                <span className="font-medium">
                                    {contact.email}
                                </span>
                                {contact.name && (
                                    <span className="text-base-content/50 ml-2">
                                        ({contact.name})
                                    </span>
                                )}
                            </div>
                            <span className="text-[10px] text-base-content/40 ml-2 shrink-0">
                                {contact.source === 'client'
                                    ? 'Cliente'
                                    : contact.source === 'partner'
                                        ? 'Parceiro'
                                        : ''}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default function ComposeModal({
    isOpen,
    onClose,
    onSent,
    accounts,
    mode,
    originalMessage,
    draftData,
}: ComposeModalProps) {
    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
        null,
    )
    const [toField, setToField] = useState('')
    const [ccField, setCcField] = useState('')
    const [bccField, setBccField] = useState('')
    const [subject, setSubject] = useState('')
    const [bodyHtml, setBodyHtml] = useState('')
    const [attachments, setAttachments] = useState<File[]>([])
    const [showCcBcc, setShowCcBcc] = useState(false)
    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('')
    const [signature, setSignature] = useState('')
    const [sending, setSending] = useState(false)
    const [savingDraft, setSavingDraft] = useState(false)
    const [localDraftId, setLocalDraftId] = useState<number | null>(null)
    const [editorHeight, setEditorHeight] = useState(DEFAULT_EDITOR_HEIGHT)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const modalRef = useRef<HTMLDialogElement>(null)

    // Abrir/fechar modal
    useEffect(() => {
        const dialog = modalRef.current
        if (!dialog) return
        if (isOpen) {
            if (!dialog.open) dialog.showModal()
        } else {
            dialog.close()
        }
    }, [isOpen])

    // Inicializar campos quando modal abre
    useEffect(() => {
        if (!isOpen) return

        // Carregar templates
        getEmailTemplates().then(res => {
            if (res.result) setTemplates(res.result)
        })

        if (draftData) {
            setSelectedAccountId(draftData.emailAccountId)
            setToField(draftData.toAddresses || '')
            setCcField(draftData.ccAddresses || '')
            setBccField(draftData.bccAddresses || '')
            setSubject(draftData.subject || '')
            setBodyHtml(draftData.bodyHtml || '')
            setLocalDraftId(draftData.id)
            if (draftData.ccAddresses || draftData.bccAddresses) {
                setShowCcBcc(true)
            }
            return
        }

        // Resetar campos
        setToField('')
        setCcField('')
        setBccField('')
        setSubject('')
        setBodyHtml('')
        setAttachments([])
        setShowCcBcc(false)
        setSelectedTemplateId('')
        setSignature('')
        setLocalDraftId(null)
        setEditorHeight(DEFAULT_EDITOR_HEIGHT)

        const defaultAccountId =
            accounts.length > 0 ? accounts[0].id : null

        if (originalMessage) {
            const accountId =
                originalMessage.emailAccount?.id || defaultAccountId
            setSelectedAccountId(accountId)

            if (mode === 'reply') {
                setToField(originalMessage.fromAddress || '')
                setSubject(
                    originalMessage.subject?.startsWith('Re:')
                        ? originalMessage.subject
                        : `Re: ${originalMessage.subject || ''}`,
                )
                setBodyHtml(buildQuotedHtml(originalMessage))
            } else if (mode === 'replyAll') {
                const allTo = parseAddresses(originalMessage.toAddresses)
                const allCc = parseAddresses(originalMessage.ccAddresses)
                const ownEmail = accounts.find(
                    a => a.id === accountId,
                )?.email
                const replyTo = [
                    originalMessage.fromAddress || '',
                    ...allTo,
                ].filter(e => e && e !== ownEmail)
                const replyCc = allCc.filter(e => e !== ownEmail)

                setToField(replyTo.join(', '))
                if (replyCc.length > 0) {
                    setCcField(replyCc.join(', '))
                    setShowCcBcc(true)
                }
                setSubject(
                    originalMessage.subject?.startsWith('Re:')
                        ? originalMessage.subject
                        : `Re: ${originalMessage.subject || ''}`,
                )
                setBodyHtml(buildQuotedHtml(originalMessage))
            } else if (mode === 'forward') {
                setSelectedAccountId(accountId)
                setSubject(
                    originalMessage.subject?.startsWith('Fwd:')
                        ? originalMessage.subject
                        : `Fwd: ${originalMessage.subject || ''}`,
                )
                const fwdHeader = `
                    <br/><br/>
                    <div style="border-top:1px solid #ccc;padding-top:12px;">
                        <p style="margin:0 0 4px;font-size:12px;color:#555;">---------- Mensagem encaminhada ----------</p>
                        <p style="margin:0 0 2px;font-size:12px;color:#555;">De: ${originalMessage.fromName || ''} &lt;${originalMessage.fromAddress || ''}&gt;</p>
                        <p style="margin:0 0 2px;font-size:12px;color:#555;">Data: ${originalMessage.receivedAt ? new Date(originalMessage.receivedAt).toLocaleString('pt-BR') : ''}</p>
                        <p style="margin:0 0 2px;font-size:12px;color:#555;">Assunto: ${originalMessage.subject || ''}</p>
                        <p style="margin:0 0 8px;font-size:12px;color:#555;">Para: ${originalMessage.toAddresses || ''}</p>
                        ${originalMessage.bodyHtml || `<pre>${originalMessage.bodyText || ''}</pre>`}
                    </div>
                `
                setBodyHtml(fwdHeader)
            }
        } else {
            setSelectedAccountId(defaultAccountId)
        }
    }, [isOpen, mode, originalMessage, draftData, accounts])

    // Carregar assinatura ao trocar conta
    const loadSignature = useCallback(
        async (accountId: number) => {
            const res = await getAccountSignature(accountId)
            if (res.result?.signatureHtml) {
                setSignature(res.result.signatureHtml)
            } else {
                setSignature('')
            }
        },
        [],
    )

    useEffect(() => {
        if (selectedAccountId && isOpen) {
            loadSignature(selectedAccountId)
        }
    }, [selectedAccountId, isOpen, loadSignature])

    // Auto-save rascunho a cada 30s
    useEffect(() => {
        if (!isOpen || mode !== 'compose') return

        autoSaveRef.current = setInterval(() => {
            if (selectedAccountId && (toField || subject || bodyHtml)) {
                handleSaveDraft(true)
            }
        }, 30000)

        return () => {
            if (autoSaveRef.current) clearInterval(autoSaveRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, mode, selectedAccountId, toField, subject, bodyHtml])

    const handleSaveDraft = async (silent = false) => {
        if (!selectedAccountId) {
            if (!silent) toast.warning('Selecione uma conta de email')
            return
        }
        setSavingDraft(true)
        try {
            const res = await saveDraft({
                accountId: selectedAccountId,
                draftId: localDraftId ?? undefined,
                to: toField
                    ? toField.split(',').map(s => s.trim())
                    : undefined,
                cc: ccField
                    ? ccField.split(',').map(s => s.trim())
                    : undefined,
                bcc: bccField
                    ? bccField.split(',').map(s => s.trim())
                    : undefined,
                subject: subject || undefined,
                bodyHtml: bodyHtml || undefined,
            })
            if (res.result) {
                setLocalDraftId(res.result.id)
                if (!silent) toast.success('Rascunho salvo')
            }
        } catch {
            if (!silent) toast.error('Erro ao salvar rascunho')
        } finally {
            setSavingDraft(false)
        }
    }

    const handleSend = async () => {
        if (!selectedAccountId) {
            toast.warning('Selecione uma conta de email')
            return
        }

        // Validacoes por modo
        if (mode === 'compose' || mode === 'forward') {
            if (!toField.trim()) {
                toast.warning('Informe pelo menos um destinatário')
                return
            }
            if (mode === 'compose' && !subject.trim()) {
                toast.warning('Informe o assunto')
                return
            }
        }

        setSending(true)
        try {
            let success = false

            if (localDraftId && mode === 'compose') {
                // Enviar rascunho existente
                const formData = new FormData()
                attachments.forEach(f => formData.append('attachments', f))
                const res = await sendDraftEmail(localDraftId, formData)
                success = !!res.result?.success
            } else if (mode === 'reply' || mode === 'replyAll') {
                if (!originalMessage) return
                const formData = new FormData()
                formData.append('messageId', String(originalMessage.id))
                formData.append(
                    'bodyHtml',
                    signature
                        ? `${bodyHtml}<br/><div class="signature">${signature}</div>`
                        : bodyHtml,
                )
                formData.append(
                    'replyAll',
                    mode === 'replyAll' ? 'true' : 'false',
                )
                if (ccField) {
                    ccField
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(e => formData.append('cc[]', e))
                }
                if (bccField) {
                    bccField
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(e => formData.append('bcc[]', e))
                }
                attachments.forEach(f => formData.append('attachments', f))
                const res = await replyToEmail(formData)
                success = !!res.result?.success
            } else if (mode === 'forward') {
                if (!originalMessage) return
                const formData = new FormData()
                formData.append('messageId', String(originalMessage.id))
                toField
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .forEach(e => formData.append('to[]', e))
                if (ccField) {
                    ccField
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(e => formData.append('cc[]', e))
                }
                const additionalText = signature
                    ? `${bodyHtml}<br/><div class="signature">${signature}</div>`
                    : bodyHtml
                formData.append('additionalText', additionalText)
                attachments.forEach(f => formData.append('attachments', f))
                const res = await forwardEmail(formData)
                success = !!res.result?.success
            } else {
                // compose
                const formData = new FormData()
                formData.append('accountId', String(selectedAccountId))
                toField
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .forEach(e => formData.append('to[]', e))
                if (ccField) {
                    ccField
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(e => formData.append('cc[]', e))
                }
                if (bccField) {
                    bccField
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                        .forEach(e => formData.append('bcc[]', e))
                }
                formData.append('subject', subject)
                formData.append(
                    'bodyHtml',
                    signature
                        ? `${bodyHtml}<br/><div class="signature">${signature}</div>`
                        : bodyHtml,
                )
                attachments.forEach(f => formData.append('attachments', f))
                const res = await sendEmail(formData)
                success = !!res.result?.success
            }

            if (success) {
                toast.success('Email enviado com sucesso')
                onSent()
                onClose()
            } else {
                toast.error('Erro ao enviar email')
            }
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : 'Erro ao enviar email'
            if (message.includes('escopo') || message.includes('403')) {
                toast.error(
                    'Permissão insuficiente. Reconecte a conta de email para habilitar o envio.',
                )
            } else {
                toast.error(message)
            }
        } finally {
            setSending(false)
        }
    }

    const handleTemplateChange = (templateId: number | '') => {
        setSelectedTemplateId(templateId)
        if (!templateId) return
        const tpl = templates.find(t => t.id === templateId)
        if (tpl) {
            if (tpl.subject && !subject) setSubject(tpl.subject)
            setBodyHtml(tpl.bodyHtml)
        }
    }

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return
        const newFiles = Array.from(files)
        const totalSize = [...attachments, ...newFiles].reduce(
            (acc, f) => acc + f.size,
            0,
        )
        if (totalSize > 25 * 1024 * 1024) {
            toast.warning('O tamanho total dos anexos não pode exceder 25 MB')
            return
        }
        setAttachments(prev => [...prev, ...newFiles])
    }

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index))
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        handleFileSelect(e.dataTransfer.files)
    }

    const getModeTitle = () => {
        switch (mode) {
            case 'reply':
                return 'Responder'
            case 'replyAll':
                return 'Responder a Todos'
            case 'forward':
                return 'Encaminhar'
            default:
                return 'Novo Email'
        }
    }

    if (!isOpen) return null

    return (
        <>
            <dialog
                ref={modalRef}
                className="modal z-50"
                onClose={onClose}
            >
                <ModalContainer size="md2" className="!overflow-visible">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-base-200 pb-3 mb-4">
                        <h3 className="text-lg font-semibold">
                            {getModeTitle()}
                        </h3>
                        <button
                            onClick={onClose}
                            className="btn btn-ghost btn-sm btn-circle"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </div>

                    {/* Formulario */}
                    <div
                        className="flex flex-col gap-1.5 overflow-y-auto max-h-[70vh] pr-1"
                        onDrop={handleDrop}
                        onDragOver={e => e.preventDefault()}
                    >
                        {/* De + Para em row */}
                        <div className="flex items-end gap-2">
                            {/* Conta (De) */}
                            <div className="form-control flex-1 min-w-0">
                                <label className="label py-0.5">
                                    <span className="label-text text-xs font-semibold">
                                        De
                                    </span>
                                </label>
                                <select
                                    value={selectedAccountId || ''}
                                    onChange={e =>
                                        setSelectedAccountId(
                                            e.target.value
                                                ? Number(e.target.value)
                                                : null,
                                        )
                                    }
                                    className="select select-bordered select-sm w-full"
                                    disabled={
                                        mode === 'reply' || mode === 'replyAll'
                                    }
                                >
                                    <option value="">Selecione uma conta</option>
                                    {accounts.map(acc => (
                                        <option key={acc.id} value={acc.id}>
                                            {acc.name
                                                ? `${acc.name} <${acc.email}>`
                                                : acc.email}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Para */}
                            <div className="form-control flex-1 min-w-0">
                                <label className="label py-0.5">
                                    <span className="label-text text-xs font-semibold">
                                        Para{' '}
                                    </span>
                                    {!showCcBcc && (
                                        <button
                                            type="button"
                                            onClick={() => setShowCcBcc(true)}
                                            className="label-text-alt text-xs text-secondary cursor-pointer hover:underline"
                                        >
                                            Cc / Cco
                                        </button>
                                    )}
                                </label>
                                <EmailInput
                                    value={toField}
                                    onChange={setToField}
                                    placeholder="destinatario@email.com, outro@email.com"
                                />
                            </div>
                        </div>

                        {/* CC / BCC */}
                        {showCcBcc && (
                            <div className="flex items-end gap-2">
                                <div className="form-control flex-1 min-w-0">
                                    <label className="label py-0.5">
                                        <span className="label-text text-xs font-semibold">
                                            Cc
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowCcBcc(false)
                                            }
                                            className="label-text-alt"
                                        >
                                            <MdExpandLess className="text-base" />
                                        </button>
                                    </label>
                                    <EmailInput
                                        value={ccField}
                                        onChange={setCcField}
                                        placeholder="cc@email.com"
                                    />
                                </div>
                                <div className="form-control flex-1 min-w-0">
                                    <label className="label py-0.5">
                                        <span className="label-text text-xs font-semibold">
                                            Cco
                                        </span>
                                    </label>
                                    <EmailInput
                                        value={bccField}
                                        onChange={setBccField}
                                        placeholder="cco@email.com"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Assunto + Modelo em row */}
                        <div className="flex items-end gap-2">
                            <div className="form-control flex-1 min-w-0">
                                <label className="label py-0.5">
                                    <span className="label-text text-xs font-semibold">
                                        Assunto
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Assunto do email"
                                    className="input input-bordered input-sm w-full"
                                />
                            </div>

                            {mode === 'compose' && templates.length > 0 && (
                                <div className="form-control w-48 shrink-0">
                                    <label className="label py-0.5">
                                        <span className="label-text text-xs font-semibold">
                                            Modelo
                                        </span>
                                    </label>
                                    <select
                                        value={selectedTemplateId}
                                        onChange={e =>
                                            handleTemplateChange(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : '',
                                            )
                                        }
                                        className="select select-bordered select-sm w-full"
                                    >
                                        <option value="">
                                            Modelo (opcional)
                                        </option>
                                        {templates.map(tpl => (
                                            <option key={tpl.id} value={tpl.id}>
                                                {tpl.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Corpo do email */}
                        <div className="form-control w-full">
                            <label className="label py-0.5">
                                <span className="label-text text-xs font-semibold">
                                    Mensagem
                                </span>
                            </label>
                            <RichTextEditor
                                content={bodyHtml}
                                onChange={setBodyHtml}
                                placeholder="Digite sua mensagem..."
                                minHeight={`${editorHeight}px`}
                            />
                            <button
                                type="button"
                                onClick={() =>
                                    setEditorHeight(prev => prev + 100)
                                }
                                className="btn btn-sm btn-ghost w-full mt-0 py-1.5 text-xs text-base-content/50 hover:bg-base-200 rounded-b-lg border border-t-0 border-base-300/50 transition-colors"
                            >
                                Aumentar tamanho do editor <MdExpandMore />
                            </button>
                        </div>

                        {/* Preview da assinatura */}
                        {signature && (
                            <div className="border border-base-200 rounded-lg p-3">
                                <p className="text-xs font-semibold text-base-content/50 mb-2">
                                    Assinatura
                                </p>
                                <div
                                    className="text-sm prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{
                                        __html: signature,
                                    }}
                                />
                            </div>
                        )}

                        {/* Anexos */}
                        <div className="form-control w-full border border-base-200 rounded-md">
                            <div className="flex items-center justify-between items-center border-b border-base-200 p-1">
                                <label className="label py-1">
                                    <span className="label-text text-xs font-semibold">
                                        Anexos
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    className="btn btn-ghost btn-xs gap-1"
                                >
                                    <MdAttachFile className="text-base" />
                                    Adicionar
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={e =>
                                        handleFileSelect(e.target.files)
                                    }
                                />
                            </div>

                            {attachments.length > 0 && (
                                <div className="flex flex-col gap-1 mt-1">
                                    {attachments.map((file, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center justify-between bg-base-200 rounded-lg px-3 py-1.5 text-sm"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <MdAttachFile className="text-base shrink-0" />
                                                <span className="truncate">
                                                    {file.name}
                                                </span>
                                                <span className="text-xs text-base-content/50 shrink-0">
                                                    {formatFileSize(file.size)}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeAttachment(idx)
                                                }
                                                className="btn btn-ghost btn-xs btn-circle"
                                            >
                                                <MdDelete className="text-sm text-error" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {attachments.length === 0 && (
                                <p className="text-xs text-base-content/40 mt-1 p-2">
                                    Arraste arquivos aqui ou clique em
                                    "Adicionar"
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Acoes */}
                    <ModalActions>
                        <button
                            onClick={onClose}
                            className="btn btn-ghost btn-sm"
                            disabled={sending}
                        >
                            Descartar
                        </button>

                        {mode === 'compose' && (
                            <button
                                onClick={() => handleSaveDraft(false)}
                                className="btn btn-outline btn-sm gap-1"
                                disabled={sending || savingDraft}
                            >
                                <MdSave className="text-base" />
                                {savingDraft ? 'Salvando...' : 'Salvar Rascunho'}
                            </button>
                        )}

                        <button
                            onClick={handleSend}
                            className="btn btn-secondary btn-sm gap-1"
                            disabled={sending}
                        >
                            <MdSend className="text-base" />
                            {sending ? 'Enviando...' : 'Enviar'}
                        </button>
                    </ModalActions>
                </ModalContainer>

                <form method="dialog" className="modal-backdrop">
                    <button onClick={onClose}>close</button>
                </form>
            </dialog>

            <BackdropLoading visible={sending} />
        </>
    )
}
